import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";
import { queryKeys } from "@/lib/queryKeys";
import { CHAT_SESSION_KEYS } from '@/lib/constants';
import { useApiMutation } from "@/hooks/useApiMutation";

interface Workspace {
  _id: string;
  name: string;
  description?: string;
  type: 'personal' | 'team' | 'admin';
  role: 'owner' | 'admin' | 'member' | 'viewer';
  permissions: {
    recipes: { read: boolean; write: boolean; delete: boolean };
    ingredients: { read: boolean; write: boolean; delete: boolean };
    materials: { read: boolean; write: boolean; delete: boolean };
    equipment: { read: boolean; write: boolean; delete: boolean };
    inventory: { read: boolean; write: boolean; delete: boolean };
    suppliers: { read: boolean; write: boolean; delete: boolean };
    settings: { read: boolean; write: boolean };
  };
  accessType: 'owner' | 'invited';
  subscriptionStatus?: string;
}

interface WorkspaceContextValue {
  /** Not provided by WorkspaceProvider at runtime (always undefined); declared for legacy consumers that destructure it. */
  user?: any;
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  isLoading: boolean;
  switchWorkspace: (workspaceId: string) => void;
  createWorkspace: (name: string, description?: string) => Promise<void>;
  updateWorkspace: (workspaceId: string, updates: { name?: string; description?: string }) => Promise<void>;
  hasPermission: (resource: string, action: string) => boolean;
  refreshWorkspaces: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

const STORAGE_KEY = "current-workspace-id";

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY);
  });
  const [isSwitching, setIsSwitching] = useState(false);
  // True once we've reconciled queries that may have fired before the
  // first /api/workspaces response landed (and thus without x-workspace-id).
  const queriesReconciledRef = useRef(false);

  // Fetch user's workspaces - only when authenticated
  const { data: workspaces = [], isLoading, refetch } = useQuery<Workspace[]>({
    queryKey: queryKeys.workspaces.all(),
    queryFn: async () => {
      try {
        const result = await apiRequest('GET', '/api/workspaces') as Workspace[];
        const workspacesList = result || [];
        // Store workspaces in localStorage for header validation
        localStorage.setItem('workspaces', JSON.stringify(workspacesList));
        return workspacesList;
      } catch {
        localStorage.setItem('workspaces', JSON.stringify([]));
        return []; // Return empty array on error
      }
    },
    retry: false,
    enabled: isAuthenticated && !!user, // Only run when user is authenticated
    staleTime: 10 * 60 * 1000, // Cache workspaces for 10 minutes
  });

  // Get current workspace - ensure workspaces is always an array
  const safeWorkspaces = Array.isArray(workspaces) ? workspaces : [];
  const currentWorkspace = safeWorkspaces.find(w => w._id === currentWorkspaceId) || safeWorkspaces[0] || null;

  // Auto-select first workspace if none selected
  useEffect(() => {
    const safeWorkspaces = Array.isArray(workspaces) ? workspaces : [];

    if (safeWorkspaces.length > 0 && !currentWorkspaceId) {
      // Priority: 1) Active personal workspace, 2) Any active team workspace, 3) First available
      const activePersonal = safeWorkspaces.find(w =>
        w.type === 'personal' &&
        w.subscriptionStatus === 'active'
      );
      const activeTeam = safeWorkspaces.find(w =>
        w.type === 'team' &&
        w.subscriptionStatus === 'active'
      );
      const defaultWorkspace = activePersonal || activeTeam || safeWorkspaces[0];

      setCurrentWorkspaceId(defaultWorkspace._id);
      localStorage.setItem(STORAGE_KEY, defaultWorkspace._id);
    } else if (currentWorkspaceId && !safeWorkspaces.find(w => w._id === currentWorkspaceId) && safeWorkspaces.length > 0) {
      // Current workspace no longer exists, switch to first available
      setCurrentWorkspaceId(safeWorkspaces[0]._id);
      localStorage.setItem(STORAGE_KEY, safeWorkspaces[0]._id);
    }
  }, [workspaces, currentWorkspaceId]);

  // Once we know the workspace id for this session, refresh any data
  // queries that may have fired before x-workspace-id was available.
  // Without this, sibling components that mounted in parallel with the
  // workspaces fetch end up cached against personal-workspace defaults
  // (or stale data) and never recover until the user navigates.
  useEffect(() => {
    if (queriesReconciledRef.current) return;
    if (!isAuthenticated || !user) return;
    if (isLoading) return;
    if (!currentWorkspaceId) return;
    queriesReconciledRef.current = true;
    const workspacesKey = queryKeys.workspaces.all()[0];
    queryClient.invalidateQueries({
      predicate: (q) => {
        const key = q.queryKey?.[0];
        return typeof key === 'string' && key !== workspacesKey;
      },
    });
  }, [isAuthenticated, user, isLoading, currentWorkspaceId, queryClient]);

  // Reset the reconcile flag on sign-out so the next sign-in
  // triggers the invalidation again.
  useEffect(() => {
    if (!isAuthenticated) queriesReconciledRef.current = false;
  }, [isAuthenticated]);

  // Switch workspace
  const switchWorkspace = useCallback(async (workspaceId: string) => {
    const workspace = safeWorkspaces.find(w => w._id === workspaceId);
    setIsSwitching(true);
    localStorage.setItem(STORAGE_KEY, workspaceId);
    setCurrentWorkspaceId(workspaceId);
    // Clear chat session so new workspace starts fresh
    sessionStorage.removeItem(CHAT_SESSION_KEYS.CONVERSATION_ID);
    sessionStorage.removeItem(CHAT_SESSION_KEYS.OPEN);
    await queryClient.invalidateQueries();
    window.location.href = workspace?.type === 'admin' ? '/admin/dashboard' : '/dashboard';
  }, [queryClient, safeWorkspaces]);

  // Create workspace mutation
  const createWorkspaceMutation = useApiMutation({
    endpoint: '/api/workspaces',
    method: 'POST',
    invalidate: [queryKeys.workspaces.all()],
    onSuccess: (newWorkspace: any) => {
      switchWorkspace(newWorkspace._id);
    },
  });

  // Update workspace mutation
  const updateWorkspaceMutation = useApiMutation({
    endpoint: (vars: { workspaceId: string }) => `/api/workspaces/${vars.workspaceId}`,
    method: 'PATCH',
    invalidate: [queryKeys.workspaces.all()],
  });

  // Create workspace function
  const createWorkspace = useCallback(async (name: string, description?: string) => {
    await createWorkspaceMutation.mutateAsync({ name, description });
  }, [createWorkspaceMutation]);

  // Update workspace function
  const updateWorkspace = useCallback(async (workspaceId: string, updates: { name?: string; description?: string }) => {
    await updateWorkspaceMutation.mutateAsync({ workspaceId, ...updates });
  }, [updateWorkspaceMutation]);

  // Check permissions
  const hasPermission = useCallback((resource: string, action: string) => {
    if (!currentWorkspace) return false;
    return (currentWorkspace.permissions as any)?.[resource]?.[action] || false;
  }, [currentWorkspace]);

  // Refresh workspaces
  const refreshWorkspaces = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <WorkspaceContext.Provider value={{
      workspaces: Array.isArray(workspaces) ? workspaces : [],
      currentWorkspace,
      isLoading,
      switchWorkspace,
      createWorkspace,
      updateWorkspace,
      hasPermission,
      refreshWorkspaces,
    }}>
      {isSwitching && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-xs z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Switching workspace...</p>
          </div>
        </div>
      )}
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return context;
}
